import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

interface IApiEnvelope<T> {
  data: T;
  message?: string;
  meta?: unknown;
}

const BASE = '/api/v1/contacil/empresas';

/* ---------- Saúde (lista cross-empresa / centro de ação) ---------- */

export interface IEmpresaSaude {
  empresa_id: number;
  external_id?: string | null;
  razao_social?: string | null;
  nome?: string | null;
  cnpj?: string | null;
  regime?: string | null;
  is_simples: boolean;
  inativa: boolean;
  lancamentos_total: number;
  conciliados: number;
  pendentes: number;
  valor_pendente: number;
  percent_conciliado: number;
  qtd_contas: number;
  qtd_sem_poc: number;
}

export interface IEmpresaSaudeResponse {
  itens: IEmpresaSaude[];
  total_empresas: number;
  valor_pendente_total: number;
  percent_medio: number;
  contas_sem_poc_total: number;
}

/**
 * Saúde da conciliação por empresa (ordenada por R$ pendente desc). É a porta
 * de entrada da tela Empresas — só empresas com lançamentos aparecem.
 */
export const useContaCilEmpresasSaude = () =>
  useQuery({
    queryKey: ['contacil', 'empresas-saude'] as const,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IEmpresaSaudeResponse> => {
      const res = await api.get<IApiEnvelope<IEmpresaSaudeResponse>>(`${BASE}/saude`);
      return res.data.data;
    },
  });

/* ---------- Agência × banco (gráfico) ---------- */

export interface IAgenciaBanco {
  banco: string;
  agencia?: string | null;
  qtd: number;
  soma: number;
}

/**
 * Volume (R$) por banco × agência da empresa. Hoje pode vir vazio — o ETL
 * ainda não envia agência; popula conforme novos extratos forem importados.
 */
export const useContaCilAgencias = (empresaId?: number | null) =>
  useQuery({
    queryKey: ['contacil', 'agencias', empresaId ?? null] as const,
    enabled: empresaId != null,
    queryFn: async (): Promise<IAgenciaBanco[]> => {
      const res = await api.get<IApiEnvelope<IAgenciaBanco[]>>(`${BASE}/${empresaId}/agencias`);
      return res.data.data;
    },
  });

/* ---------- Contexto IA (sócios + config) ---------- */

export interface ISocio {
  nome?: string | null;
  cpf_cnpj?: string | null;
  participacao?: number | null;
  quotas?: number | null;
  cargo?: string | null;
  data_ingresso?: string | null;
  data_saida?: string | null;
}

export interface IEmpresaConfig {
  poc_transitoria_codigo?: string | null;
  poc_transitoria_nome?: string | null;
  contexto_ia?: Record<string, unknown> | null;
}

export interface IEmpresaContexto {
  empresa_id: number;
  razao_social?: string | null;
  nome?: string | null;
  cnpj?: string | null;
  external_id?: string | null;
  regime?: string | null;
  cidade?: string | null;
  uf?: string | null;
  tem_plano: boolean;
  socios: ISocio[];
  config: IEmpresaConfig;
}

export const useContaCilEmpresaContexto = (empresaId?: number | null) =>
  useQuery({
    queryKey: ['contacil', 'empresa-contexto', empresaId ?? null] as const,
    enabled: empresaId != null,
    queryFn: async (): Promise<IEmpresaContexto> => {
      const res = await api.get<IApiEnvelope<IEmpresaContexto>>(`${BASE}/${empresaId}/contexto`);
      return res.data.data;
    },
  });

export interface IEmpresaConfigPayload {
  poc_transitoria_codigo?: string | null;
  contexto_ia?: Record<string, unknown> | null;
}

export const useSalvarEmpresaConfig = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      empresaId: number;
      payload: IEmpresaConfigPayload;
    }): Promise<IEmpresaConfig> => {
      const res = await api.put<IApiEnvelope<IEmpresaConfig>>(
        `${BASE}/${params.empresaId}/config`,
        params.payload,
      );
      return res.data.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ['contacil', 'empresa-contexto', vars.empresaId],
      });
    },
  });
};

/* ---------- Memória aprendida (reusa /contacil/regras/memoria) ---------- */

export interface IMemoriaItem {
  contraparte: string;
  dc: 'D' | 'C';
  poc_codigo: string;
  poc_classificacao?: string | null;
  suporte: number;
  pureza: number;
  origem: 'razao' | 'correcao';
  status: 'confiavel' | 'quarentena';
  conflito: boolean;
}

export interface IMemoriaResumo {
  total: number;
  confiavel: number;
  conflito: number;
  correcao: number;
}

export interface IMemoriaResponse {
  itens: IMemoriaItem[];
  resumo: IMemoriaResumo;
}

export const useContaCilMemoria = (empresaId?: number | null, apenasConfiavel = false) =>
  useQuery({
    queryKey: ['contacil', 'memoria', empresaId ?? null, apenasConfiavel] as const,
    enabled: empresaId != null,
    queryFn: async (): Promise<IMemoriaResponse> => {
      const res = await api.get<IApiEnvelope<IMemoriaResponse>>('/api/v1/contacil/regras/memoria', {
        params: { empresa_id: empresaId as number, apenas_confiavel: apenasConfiavel },
      });
      return res.data.data;
    },
  });
